import { FeaturedBannerTransformer } from './FeaturedBannerTransformer';
import { NoResultsPlaceholderProps } from '@jbhi-fi/components/src/NoResultsPlaceholder/NoResultsPlaceholder';
import { ContentfulCardGrid, ContentfulSection } from '@jbhi-fi/apis/src/contentful/types';
import { CardGridProps } from '@jbhi-fi/components/src/Card';
import { CardGridCardProps } from '@jbhi-fi/components/src/Card/CardGrid/CardGrid';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { DEFAULT_SECTION_LAYOUT } from './SectionTransformer';
import type { ContentfulCardGridProps } from '../CardGrid/ContentfulCardGrid';
import { toCardGridParentPageFilter, toContentfulCardFilters } from './CardGroupTransformer';

export type ContentfulApiCardGridToContentfulCardGridPropsOptions = {
  cardGridProps?: Omit<CardGridProps, 'cards'>;
  featuredBannerCardIndex?: number;
  section?: ContentfulSection;
};

export const contentfulApiCardGridToContentfulCardGridProps = (
  cardGrid: ContentfulCardGrid,
  options: ContentfulApiCardGridToContentfulCardGridPropsOptions = {}
): ContentfulCardGridProps => {
  const {
    cardGridProps = options.cardGridProps ?? {},
    featuredBannerCardIndex = 5,
    section = {} as ContentfulSection,
  } = options;
  const { fields } = cardGrid;
  const { layout = DEFAULT_SECTION_LAYOUT } = section.fields ?? {};

  const hasFeaturedBanner = !!fields?.featuredBanner;
  const featuredBannerCard: CardGridCardProps | null = fields?.featuredBanner?.fields
    ? FeaturedBannerTransformer.toCardProps(fields.featuredBanner)
    : hasFeaturedBanner
      ? { cover: null, isLoading: true }
      : null;

  return {
    numCards: hasFeaturedBanner ? (fields?.cardCount ?? 0) - 1 : (fields?.cardCount ?? 0),
    useLoadingSkeletons: true,
    filters: fields?.parentPage?.sys?.id ? contentfulApiCardGridToContentfulFilters(cardGrid) : [],
    featuredBannerCard,
    featuredBannerCardIndex,
    cardGridProps,
    noResultsPageTitle: section.fields?.heading.toLowerCase(),
    enableContainerBreakpoints: layout !== DEFAULT_SECTION_LAYOUT,
  };
};

export const getNoCardsPlaceholderProps = (pageTitle?: string): NoResultsPlaceholderProps => {
  if (!pageTitle) {
    return {
      icon: 'LampIllustrated',
      title: 'Nothing here... yet!',
      contents: "The lights are on but no one's home for this category. Come back soon.",
    };
  }
  return {
    icon: 'LampIllustrated',
    title: `No ${pageTitle.toLowerCase()} here... yet!`,
    contents: `The lights are on but no ${pageTitle.toLowerCase()} are home for this category. Come back soon.`,
  };
};

const contentfulApiCardGridToContentfulFilters = ({ fields }: ContentfulCardGrid): Array<ContentfulFilter> => {
  return toContentfulCardFilters({
    filter: toCardGridParentPageFilter({ parentPage: fields?.parentPage }),
    fromLastXDays: fields?.fromLastXDays,
  });
};
